import React, { useState, useMemo, useEffect } from "react";
import { Box, IconButton, Chip as MuiChip, Tabs, Tab } from "@mui/material";
import { Card, TabsWrapper } from "../style";
import { styled, useTheme } from "@mui/system";
import { CarCrash, SafetyCheck } from "@mui/icons-material";
import CVT from "../../rcautoDetailsProfile2/tabs/preventivi/CVT";
import RCA from "../../rcautoDetailsProfile2/tabs/preventivi/RCA";
import AssStradale from "../../rcautoDetailsProfile2/tabs/preventivi/AssStradale";
import Personale from "../../rcautoDetailsProfile2/tabs/preventivi/Personale";
import Utilities from "../../rcautoDetailsProfile2/tabs/preventivi/Utilities";
import TCM from "../../rcautoDetailsProfile2/tabs/preventivi/TCM";
import Casa from "../../rcautoDetailsProfile2/tabs/preventivi/Casa";
import VitaProtetta from "../../rcautoDetailsProfile2/tabs/preventivi/VitaProtetta";
import { useFormRCAutoDetails } from "../../rcautoDetailsProfile2/rcautoContext";

export const displayKeys = {
  massimale: "Massimale",
  guida: "Tipo di guida",
  include: "Include",
  idPreventivo: "Numero di preventivo",
  note: "Note",
  accordo: "Accordi",
};

export const formatPrice = (price) => {
  return parseFloat(price).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
};

export const CardContainer = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})(({ theme, isExpanded, color }) => ({
  width: "100%",
  transition: "all .3s linear",
  borderRadius: 0,
  height: "auto",
  overflow: "hidden",
  border: "2px solid transparent",
  "&:hover": {
    boxShadow: color
      ? `#${color} 0px 8px 5px -2px`
      : "#407ad6 0px 8px 5px -2px",
  },
  boxShadow: color ? `#${color} 0px 3px 5px -2px` : "#407ad6 0px 3px 5px -2px",
}));

export const FullWidthCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  // alignItems: "center",
  borderRadius: 0,
  width: "100%",
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
  position: "relative",
}));

export const KeyValueRow = styled(Box)(({ theme, isEven }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(1),
  backgroundColor: isEven
    ? theme.palette.mode === "light"
      ? "#eaebedff"
      : "rgba(255,255,255,.05)"
    : "transparent",
}));

export const SmallButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})(({ theme, $isActive }) => ({
  backgroundColor: $isActive ? "#FE6A00" : "transparent",
  color: $isActive ? "white" : "inherit",
  fontSize: "0.75rem",
  borderRadius: 0,
  padding: theme.spacing(0.5, 1),
  "&:hover": {
    backgroundColor: "#FE6A00",
    color: "white",
  },
}));

export const Chip = styled(MuiChip)`
  height: 18px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"]};
  color: ${(props) => props.theme.palette.common.white};
`;

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const PersonaTabPanel = ({ activeSubTab, onSubTabChange }) => {
  const theme = useTheme();
  const subTabs = ["Utilities", "TCM", "Casa", "Vita protetta"];

  const renderSubTabContent = (tabName) => {
    switch (tabName) {
      case "Utilities":
        return <Utilities />;
      case "TCM":
        return <TCM />;
      case "Casa":
        return <Casa />;
      case "Vita protetta":
        return <VitaProtetta />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={activeSubTab}
        onChange={(_, newValue) => onSubTabChange(newValue)}
        sx={{
          mb: 3,
          backgroundColor:
            theme.palette.mode === "light" ? "#f5f5f5" : "#1a2332",
          borderRadius: 1,
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {subTabs.map((tabName) => (
          <Tab
            key={tabName}
            label={tabName}
            value={tabName}
          />
        ))}
      </Tabs>
      {subTabs.map((tabName) => (
        <CustomTabPanel key={tabName} value={activeSubTab} index={tabName}>
          {renderSubTabContent(tabName)}
        </CustomTabPanel>
      ))}
    </Box>
  );
};

const Preventivi = ({ defaultTab, rcAutoGetDetails }) => {
  const theme = useTheme();
  const { formState } = useFormRCAutoDetails();
  const tabs = formState.preventivi;
  const [activeSubTab, setActiveSubTab] = useState("Utilities");

  // Extract available tabs with non-empty lists
  const availableTabs = useMemo(() => {
    const backendTabs = Object.entries(tabs || {})
      .map(([key, value]) => ({
        [key]: value,
      }))
      ?.filter((group) => {
        const key = Object.keys(group)[0];
        return group[key] && group[key].length > 0;
      })
      .map((group) => Object.keys(group)[0]);

    // TODO: Rimuovere queste righe quando il backend sarà pronto
    // Aggiungo temporaneamente le tab per testing
    const additionalTabs = [
      "Personale",
      "Persona",
    ];
    return [...backendTabs, ...additionalTabs];
  }, [tabs]);

  // Set initial active tab
  const [activeTab, setActiveTab] = useState(
    defaultTab && availableTabs.includes(defaultTab)
      ? defaultTab
      : availableTabs[0] || "",
  );

  useEffect(() => {
    if (availableTabs.length > 0) {
      if (defaultTab && availableTabs.includes(defaultTab)) {
        if (!activeTab || activeTab === "") setActiveTab(defaultTab);
      } else if (!activeTab || !availableTabs.includes(activeTab)) {
        setActiveTab(availableTabs[0]);
      }
    }
  }, [availableTabs, defaultTab, activeTab]);

  const getTabIcon = (tabName) => {
    switch (tabName) {
      case "RCA":
        return <CarCrash />;
      case "CVT":
      case "Assistenza Stradale":
      case "Personale":
      case "Persona":
      default:
        return <SafetyCheck />;
    }
  };

  const renderTabContent = (tabName) => {
    switch (tabName) {
      case "RCA":
        return <RCA rcAutoGetDetails={rcAutoGetDetails} />;
      case "CVT":
        return <CVT />;
      case "Assistenza Stradale":
        return <AssStradale rcAutoGetDetails={rcAutoGetDetails} />;
      case "Personale":
        return <Personale />;
      case "Persona":
        return <PersonaTabPanel activeSubTab={activeSubTab} onSubTabChange={setActiveSubTab} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TabsWrapper>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mt: 2,
            mb: 4,
            backgroundColor:
              theme.palette.mode === "light" ? "#fff" : "#233044",
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {availableTabs.map((tabName) => (
            <Tab
              key={tabName}
              label={tabName}
              value={tabName}
              icon={getTabIcon(tabName)}
              iconPosition="start"
            />
          ))}
        </Tabs>
        {availableTabs.map((tabName) => (
          <CustomTabPanel key={tabName} value={activeTab} index={tabName}>
            {renderTabContent(tabName)}
          </CustomTabPanel>
        ))}
      </TabsWrapper>
    </Box>
  );
};

export default Preventivi;
